import { computed, effect, inject, Injectable, signal, WritableSignal } from '@angular/core';
import { ApplicationStatusEnum, AssignmentSummaryDto, BriefApplicationResponseDto, BriefProgressEnum, BriefResponseDto, BriefStatusEnum, SocialChannelEnum, SocialPlacementChannel } from '../../../../common/general-types/briefs.dto';
import { CreateBriefPaymentMethodDto, PaymentMethodTypeEnum } from '../../../../common/general-types/shout-plus.dto';
import { ShoutPlusBriefService } from '../../../../shout-plus/services/shout-plus-brief/shout-plus-brief.service';
import {
  getAffiliateProviderLabel,
  getAffiliateProviderLogo,
  getResolvedPaymentMethodType,
  getShoutPlusCashPrice,
  isShoutPlusBrief,
  isShoutPlusNonCashBrief,
} from '../../../../shout-plus/utils/shout-plus-hire.util';
import { ShoutBriefDto } from '../../../../common/general-types/shout-briefs.dto';
import { isShoutPlusAffiliateEnabled } from '../../../../common/app-config/shout-plus-feature-flags';
import { AlertService } from '../../../../atoms/alert/alert.service';
import { BriefService } from '../../../../services/brief/brief.service';
import { catchError, finalize, Observable, of, Subscription, take, tap } from 'rxjs';
import { influencerSizes } from '../../../../common/app-config/filter-models/tiktok-influencers';
import { ActiveFiltersModel, BriefApplicationFilterButton, FilterCreatorsDto, FilterItemDto, FilterStatsDto } from '../../../../common/general-types/filters.dto';
import { interestsList } from '../../../../common/app-config/filter-models/interests';
import { countriesList } from '../../../../common/app-config/filter-models/countries';
import { genderOptions } from '../../../../common/app-config/filter-models/gender';
import { noProductSortOptions } from '../../../../common/app-config/filter-models/sort';
import { platformsList } from '../../../../common/app-config/filter-models/platforms';
import { ToastNotificationService } from '../../../../atoms/toast-notification/toast-notification.service';
import { MagicSearchService } from '../../../creators/service/magic-search/magic-search.service';
import { CentralManagementService } from '../../../../services/central-management/central-management.service';
import { Router } from '@angular/router';
import { ActionTypesEnum } from '../../../../common/app-config/filter-models/misc';
import { BriefApplicationService } from '../../../../services/brief-application/brief-application.service';
import { HireCreatorDto } from '../../../../common/general-types/brief-application.dto';
import { HireCreatorResponseDto } from '../../../../common/general-types/hire-response.dto';
import { ThemeService } from '../../../../services/theme/theme.service';
import { FirebaseService } from '../../../../common/firebase/firebase.service';
import { WalletService } from '../../../../services/wallet/wallet.service';
import { HttpErrorResponse } from '@angular/common/http';
import { environment } from '../../../../../environments/environment';
import { PricingModelV2Service } from '../../../../services/pricing-model-v2/pricing-model-v2.service';
import { VideoFilter, VideoFilterSearch, VideoResponseDto, VideoReviewedResponseDto, VideoStatusEnum, VideoWithReviewDataResponseDto } from '../../../../common/general-types/videos.dto';
import { VideoService } from '../../../../services/video/video.service';
import { SubmittedPostDto } from '../../../../common/general-types/submitted-posts.dto';
import { LatestPostResultsDto } from '../../../../common/general-types/super-social.dto';
import { ManageResultsFilterData } from '../manage-results/manage-results.service';
import {
  isInsufficientFundsHireError,
  isInsufficientFundsHireResponse,
  openTopUpDrawerForInsufficientFunds,
} from '../../../manage-brief/utils/hire-insufficient-funds.util';
import {
  filterApplicantsByProfile,
  hasActiveApplicantProfileFilters,
  ScreeningFilterMap,
} from './applicant-profile-filter.helpers';
import { excludeHiddenAssignmentApplicants, mergeApplicantFilters, sortApplicantsClientSide } from './applicant-filters.helpers';
import { ASSIGNMENT_DEFAULT_SORT } from '../../tabs/assignment/molecules/assignment-sort/assignment-sort.helpers';
import { manageBriefCommands } from '../../manage-brief-tab.routing';
import { readChannels, togglableChannels } from '../../tabs/assignment/cross-platform-state.util';
import { socialChannelIdsOnPieces } from '@/app/common/brief-pieces/piece-rules';
import {
  filterStatsAfterHire,
  hiredBrandTotal,
  mergeHiredApplication,
  NOTHING_OWED_COPY,
  toMajor,
} from '@/app/common/brief-pieces/piece-hire-quote';
import {
  apiErrorCode,
  PieceHireQuoteStore,
  priceChangedQuote,
} from '../../../../services/brief-application/piece-hire-quote.store';
import { applyVat, crossPostNetTotal } from '../../tabs/assignment/hire-cost.util';

@Injectable({
  providedIn: 'root'
})
export class ManageBriefTabsService {


  resultsFilterDataSignal: WritableSignal<ManageResultsFilterData | undefined> = signal(undefined);


  /** Brief Variables */
  focussedBrief = signal<BriefResponseDto | undefined>(undefined);
  focussedBriefId!: string;
  focussedShoutBrief!: ShoutBriefDto;
  selectedComplianceDocs = signal<any[] | undefined>(undefined);
  brandComplianceDocs = signal<any[] | undefined>(undefined);
  /** A Briefing tab section to scroll to and highlight on arrival (e.g. 'hire' from the settings menu). */
  briefingFocus = signal<string | null>(null);
  /** Brief Variables - END */

  /** Filter Variables */
  creatorFilters!: FilterCreatorsDto;

  /** Assignment demographic filters (country, gender, age, rating, etc.) */
  applicantProfileFilters = signal<FilterCreatorsDto>({});
  /** Per screening-question filters (questionId → Yes/No/pass/fail/answered). Applied client-side. */
  applicantScreeningFilters = signal<ScreeningFilterMap>({});
  /** Assignment list sort (name / recently updated / auto-decline). */
  applicantSort = signal<FilterItemDto>(ASSIGNMENT_DEFAULT_SORT);
  /** Assignment "Needs you" tile: only creators waiting on the brand (server-side filter). */
  needsYouFilter = signal(false);
  /** Stage tile counts for the Assignment tab. */
  assignmentSummary = signal<AssignmentSummaryDto | null>(null);

  activeFilters: ActiveFiltersModel = {
    ageOptions: { min: 16, max: 60 },
    interestsOptions: interestsList,
    countryOptions: countriesList,
    genderOptions: genderOptions,
    sortOptions: noProductSortOptions,
    platformOptions: platformsList,
    tiktokSizeOptions: influencerSizes,
    instagramSizeOptions: influencerSizes,
    youtubeSizeOptions: influencerSizes,
    snapchatSizeOptions: influencerSizes,
  };

  statusFilterButtons = signal<BriefApplicationFilterButton[]>([]);
  selectedFilter = signal<ApplicationStatusEnum>(ApplicationStatusEnum.All);

  pageNumber: number = 0;
  isLoadingMore = signal<boolean>(false);
  hasMoreApplicants = signal<boolean>(true);
  private applicantsRequestSub?: Subscription;
  /** What the in-flight page-0 load asked for, so an identical refresh doesn't restart it. */
  private applicantsRefreshKey?: string;
  private applicantsRequestId = 0;
  /** Filter Variables - END */

  /** Moderation Videos Variables */
  latestApplicationVideos = signal<VideoWithReviewDataResponseDto[] | undefined>(undefined);
  videoPageNumber: number = 0;
  /** Moderation Videos Variables - END */

  /** Applicants Variables */
  filteredApplicants = signal<BriefApplicationResponseDto[] | undefined>(undefined);
  /** Total matching applicants for the current filter query (from API count). */
  applicantsTotalCount = signal<number | null>(null);
  actionsProcessing = signal<string[]>([]);
  videoStatusFilter = signal<VideoStatusEnum>(VideoStatusEnum.All);
  /** Applicants Variables - END */

  /** Creator ID from `/manage-brief/:briefId/:tab/:creatorId` when video feedback is open. */
  videoFeedbackCreatorId = signal<string | undefined>(undefined);

  /** Mirrored from ManageBriefTabsComponent for return navigation from view-submission */
  uiSelectedTab = signal<BriefProgressEnum>(BriefProgressEnum.ActivityFeed);
  uiSelectedSubTab = signal<number>(0);
  logisticsHighlightApplicationId = signal<string | null>(null);
  private viewSubmissionReturnContext: { briefId: string; tab: BriefProgressEnum; subTab: number } | null = null;

  /** Applicant distribution across the application statuses */
  filterStats = signal<FilterStatsDto>({
    'All': 0,
    'Applied': 0,
    'Invited': 0,
    'Hired': 0,
    'ReadyToPost': 0,
    'Started': 0,
    'Posted': 0,
    'Paid': 0,
    'Completed': 0
  });

  videoSearchTerm = signal<string>('');

  /** Results Variables */
  invalidSubmittedPosts = signal<SubmittedPostDto[] | undefined>(undefined);
  submittedPosts = signal<SubmittedPostDto[] | undefined>(undefined);
  selectedChannelTab = signal<number>(0);
  shoutSuperSocialResultsRes = signal<{ count: number, submittedPosts: LatestPostResultsDto[] } | undefined>(undefined);
  /** Results Variables - END */


  /** External Services */
  router = inject(Router);
  alertService = inject(AlertService);
  briefService = inject(BriefService);
  toastNotificationService = inject(ToastNotificationService);
  briefApplicationService = inject(BriefApplicationService);
  magicSearchService = inject(MagicSearchService);
  centralManagement = inject(CentralManagementService);
  firebaseService = inject(FirebaseService);
  walletService = inject(WalletService);
  pricingModelService = inject(PricingModelV2Service);
  pieceQuotes = inject(PieceHireQuoteStore);
  videoService = inject(VideoService);
  private shoutPlusBriefService = inject(ShoutPlusBriefService);
  private themeService = inject(ThemeService);
  /** External Services - END */

  readonly paymentMethod = computed(() => this.shoutPlusBriefService.briefPaymentMethodSignal());

  constructor() {
    effect(() => {
      // Fire-and-forget: keep SHOUT+ payment method in sync with the focussed brief.
      this.syncBriefPaymentMethod(this.focussedBrief()).subscribe();
    });
  }

  navigateToBriefTab(
    tab: BriefProgressEnum,
    extra?: { subTab?: number; creatorId?: string | null },
    replaceUrl = false,
  ): void {
    const briefId = this.focussedBriefId || this.focussedBrief()?.id;
    if (!briefId) {
      return;
    }
    void this.router.navigate(manageBriefCommands(briefId, tab, extra), { replaceUrl });
  }

  navigateToVideoFeedback(creatorId: string): void {
    const id = creatorId?.trim();
    if (!id) {
      return;
    }
    this.navigateToBriefTab(this.uiSelectedTab(), { creatorId: id });
  }

  clearVideoFeedbackRoute(): void {
    const subTab =
      this.uiSelectedTab() === BriefProgressEnum.Assignment && this.uiSelectedSubTab() > 0
        ? this.uiSelectedSubTab()
        : undefined;
    this.navigateToBriefTab(this.uiSelectedTab(), { subTab });
  }

  syncBriefPaymentMethod(brief: BriefResponseDto | undefined): Observable<CreateBriefPaymentMethodDto | null> {
    this.shoutPlusBriefService.briefPaymentMethodSignal.set(null);
    if (!isShoutPlusBrief(brief)) {
      return of(null);
    }

    const embedded = (
      brief!.brief_payment_method ??
      brief!.briefPaymentMethod
    ) as CreateBriefPaymentMethodDto | null | undefined;

    if (embedded?.type) {
      this.shoutPlusBriefService.briefPaymentMethodSignal.set(embedded);
      return of(embedded);
    }

    const paymentMethodId = brief!.brief_payment_method_id;
    if (paymentMethodId) {
      return this.shoutPlusBriefService.getShoutPlusPaymentMethod(paymentMethodId).pipe(
        take(1),
        tap({
          next: (paymentMethod) => this.shoutPlusBriefService.briefPaymentMethodSignal.set(paymentMethod),
          error: (err) => console.error('Failed to load SHOUT+ payment method', err),
        }),
        catchError(() => of(null)),
      );
    }

    return of(null);
  }

  isShoutPlusBrief(): boolean {
    return isShoutPlusBrief(this.focussedBrief());
  }

  isShoutPlusNonCashBrief(): boolean {
    return isShoutPlusNonCashBrief(this.focussedBrief(), this.paymentMethod());
  }

  getResolvedPaymentMethodType(): PaymentMethodTypeEnum | null {
    return getResolvedPaymentMethodType(this.focussedBrief(), this.paymentMethod());
  }

  /** The SHOUT+ results page only reports affiliate sales, so it is offered for affiliate briefs alone. */
  hasShoutPlusAffiliateResults(): boolean {
    return (
      isShoutPlusAffiliateEnabled() &&
      this.isShoutPlusBrief() &&
      this.getResolvedPaymentMethodType() === PaymentMethodTypeEnum.AFFILIATE
    );
  }

  getAffiliateProviderLogo(isDarkMode = this.themeService.isDarkMode()): string | null {
    const brief = this.focussedBrief();
    if (!brief) {
      return null;
    }
    return getAffiliateProviderLogo({
      brief_remuneration_type: brief.brief_remuneration_type,
      briefPaymentMethod: (
        brief.brief_payment_method
        ?? brief.briefPaymentMethod
        ?? this.paymentMethod()
      ) as {
        type?: string;
        affiliate_provider?: string | null;
      } | null,
    }, isDarkMode);
  }

  getAffiliateProviderLabel(): string | null {
    const brief = this.focussedBrief();
    if (!brief) {
      return null;
    }
    return getAffiliateProviderLabel({
      brief_remuneration_type: brief.brief_remuneration_type,
      briefPaymentMethod: (
        brief.brief_payment_method
        ?? brief.briefPaymentMethod
        ?? this.paymentMethod()
      ) as {
        type?: string;
        affiliate_provider?: string | null;
      } | null,
    });
  }

  getTotalPriceShoutPlus(): number {
    return getShoutPlusCashPrice(this.paymentMethod());
  }


  /**
   * Merge assignment profile filters with keyword search + status tab filters
   * from MagicSearchService into a FilterCreatorsDto for getApplicants.
   */
  buildApplicantFilters(): FilterCreatorsDto {
    const sort = this.applicantSort();
    const merged = mergeApplicantFilters(
      this.applicantProfileFilters(),
      this.magicSearchService.filtersObjectSignal(),
      {
        sortField: typeof sort.value === 'string' ? sort.value : undefined,
        sortOrder: sort.direction,
      },
    );
    if (this.needsYouFilter()) {
      merged.needsYou = true;
    }
    return merged;
  }

  /** Reload the Assignment stage tile counts. Failures leave the last counts in place. */
  loadAssignmentSummary(): void {
    const briefId = this.focussedBriefId;
    if (!briefId) {
      return;
    }
    this.briefApplicationService.getAssignmentSummary(briefId).pipe(take(1)).subscribe({
      next: (summary) => {
        if (this.focussedBriefId === briefId) {
          this.assignmentSummary.set(summary);
        }
      },
      error: (err: unknown) => console.warn('assignment summary failed', err),
    });
  }

  clearApplicantProfileFilters(): void {
    this.applicantProfileFilters.set({});
    this.applicantScreeningFilters.set({});
  }

  resetApplicantSort(): void {
    this.applicantSort.set(ASSIGNMENT_DEFAULT_SORT);
  }

  /** Rebuild filters from current state and reload applicants from page 0. */
  refreshApplicants(): void {
    if (!this.focussedBriefId) {
      return;
    }
    this.pageNumber = 0;
    const filters = this.buildApplicantFilters();
    const key = JSON.stringify([
      this.focussedBriefId,
      filters,
      this.applicantProfileFilters(),
      this.applicantScreeningFilters(),
    ]);
    if (key === this.applicantsRefreshKey && this.applicantsRequestSub && !this.applicantsRequestSub.closed) {
      return;
    }
    this.creatorFilters = filters;
    this.getBriefApplicants(this.focussedBriefId, filters, 0);
    this.applicantsRefreshKey = key;
  }

  getBriefApplicants(briefId: string, filters: FilterCreatorsDto, page: number, isLoading?: boolean) {
    // Always clone so skip/take mutation cannot leak into shared filter state.
    const requestFilters: FilterCreatorsDto = { ...filters };
    const profileFilters = this.applicantProfileFilters();
    const screeningFilters = this.applicantScreeningFilters();
    const clientFilterActive = hasActiveApplicantProfileFilters(
      profileFilters,
      screeningFilters,
    );

    // When profile/screening filters are active, load a large page and filter client-side.
    // The filter-creators API does not reliably apply rating / verified / hasReviews yet,
    // and screening answers are only available on the application payload.
    requestFilters.skip = clientFilterActive ? 0 : page * 12;
    requestFilters.take = clientFilterActive ? 500 : 12;

    if (!requestFilters.sortField) {
      requestFilters.sortField = 'applicationStatusModifiedDate';
      requestFilters.sortOrder = 'DESC';
    }

    if (page === 0 || clientFilterActive) {
      this.hasMoreApplicants.set(!clientFilterActive);
      this.filteredApplicants.set(undefined);
      this.applicantsTotalCount.set(null);
    } else {
      this.isLoadingMore.set(true);
    }

    this.applicantsRequestSub?.unsubscribe();
    this.applicantsRefreshKey = undefined;
    const requestId = ++this.applicantsRequestId;

    this.applicantsRequestSub = this.briefService.getApplicants(briefId, requestFilters).pipe(
      tap((response: { count?: number; data?: BriefApplicationResponseDto[] }) => {
        if (requestId !== this.applicantsRequestId) {
          return;
        }
        console.log('Applicants Please: ', response, requestFilters);

        const rawData = excludeHiddenAssignmentApplicants(response.data ?? []);
        const filtered = clientFilterActive
          ? filterApplicantsByProfile(rawData, profileFilters, {
              brief: this.focussedBrief(),
              screeningFilters,
            })
          : rawData;
        const data = clientFilterActive
          ? sortApplicantsClientSide(filtered, requestFilters.sortField, requestFilters.sortOrder)
          : filtered;

        if (clientFilterActive) {
          this.applicantsTotalCount.set(data.length);
          this.filteredApplicants.set(data);
          this.hasMoreApplicants.set(false);
          return;
        }

        if (typeof response.count === 'number') {
          this.applicantsTotalCount.set(response.count);
        } else if (page === 0) {
          this.applicantsTotalCount.set(data.length);
        }

        if (page === 0) {
          this.filteredApplicants.set(data);
        } else {
          this.filteredApplicants.set([
            ...(this.filteredApplicants() || []),
            ...data.filter(
              (application: BriefApplicationResponseDto) =>
                !this.filteredApplicants()?.some((x) => x.id === application.id),
            ),
          ]);
        }

        if (data.length < 12) {
          this.hasMoreApplicants.set(false);
        }
      }),
      catchError((err) => {
        console.error('Error fetching data:', err);
        if (requestId === this.applicantsRequestId && (page === 0 || clientFilterActive)) {
          this.filteredApplicants.set([]);
          this.applicantsTotalCount.set(0);
        }
        return of({ count: 0, data: [] as BriefApplicationResponseDto[] });
      }),
      finalize(() => {
        if (requestId === this.applicantsRequestId) {
          this.isLoadingMore.set(false);
        }
      }),
    ).subscribe();
  }

  getLatestVideos(isScroll: boolean = false) {
    console.log('getLatestVideos()', this.videoStatusFilter());
    if (this.focussedBriefId) {

      let statuses: number[] = [];

      if (isScroll) {
        this.videoPageNumber++;
      } else {
        this.videoPageNumber = 0;
      }

      if (this.videoStatusFilter() == VideoStatusEnum.All) {
        statuses = [VideoStatusEnum.New, VideoStatusEnum.Seen, VideoStatusEnum.Accepted];
      } else {
        statuses = [this.videoStatusFilter()]
      }

      const request: VideoFilterSearch = {
        briefId: this.focussedBriefId,
        skip: this.videoPageNumber * 10,
        take: 10,
        status: statuses,
        searchTerm: this.videoSearchTerm()
      };
      this.videoService.getLatestApplicationVideosSearch(request).subscribe({
        next: (response: VideoWithReviewDataResponseDto[]) => {
          console.log('Latest videos: ', response);
          if (isScroll) {
            this.latestApplicationVideos.set([...this.latestApplicationVideos() || [], ...response]);
          } else {
            this.latestApplicationVideos.set(response);
          }
        },
        error: (err: any) => {
          console.error('Error getting latest videos: ', err);
        }
      });
    }
  }

  editBrief() {
    // this.briefService.editingBrief = this.focussedBrief()!.id;
    // this.briefService.editBrief(this.focussedBrief()!.id)
    // this.briefService.loadingEditBrief = true;
    // this.router.navigate(['/create-brief']);
  }

  pauseResume() {
    if (this.focussedBrief()?.currentStatus.status == BriefStatusEnum.Public) {
      this.markPaused();
    } else if (this.focussedBrief()?.currentStatus.status == BriefStatusEnum.Private) {
      this.markPublic();
    }
  }

  async markPaused() {
    if (this.focussedBrief()) {
      const confirm = await this.alertService.confirm({
        title: this.focussedBrief()!.currentStatus.status == BriefStatusEnum.Public ? 'Make brief Private?' : 'Make brief Public?',
        message: `Are you sure you want to make ${this.focussedBrief()!.title} ${this.focussedBrief()!.currentStatus.status == BriefStatusEnum.Public ? 'private' : 'public'}? Creators will ${this.focussedBrief()!.currentStatus.status == BriefStatusEnum.Public ? 'not be able to see or apply to this brief' : 'be able to see and apply to this brief'}.`,
        confirmText: this.focussedBrief()!.currentStatus.status == BriefStatusEnum.Public ? 'Go Private' : 'Go Public',
        cancelText: 'Cancel',
        type: 'confirm',
        icon: 'info'
      });

      console.log('confirm: ', confirm);
      if (confirm) {
        this.briefService.pauseBrief(this.focussedBrief()!.id).pipe(take(1)).subscribe({
          next: briefPausedRes => {
            console.log('briefPausedRes: ', briefPausedRes);
            this.focussedBrief.set(briefPausedRes);
          },
          error: err => {
            console.error('briefPausedRes ERROR: ', err);
          }
        });
      }
    }
  }

  async markPublic() {
    if (this.focussedBrief()) {
      const confirm = await this.alertService.confirm({
        title: this.focussedBrief()!.currentStatus.status == BriefStatusEnum.Public ? 'Make brief Private?' : 'Make brief Public?',
        message: `Are you sure you want to make ${this.focussedBrief()!.title} ${this.focussedBrief()!.currentStatus.status == BriefStatusEnum.Private ? 'public' : 'private'}? Creators will ${this.focussedBrief()!.currentStatus.status == BriefStatusEnum.Private ? 'be able to see and apply to this brief' : 'not be able to see or apply to this brief'}.`,
        confirmText: this.focussedBrief()!.currentStatus.status == BriefStatusEnum.Public ? 'Go Private' : 'Go Public',
        cancelText: 'Cancel',
        type: 'confirm',
        icon: 'info'
      });

      console.log('confirm: ', confirm);
      if (confirm) {
        this.briefService.publicBrief(this.focussedBrief()!.id).pipe(take(1)).subscribe({
          next: briefPausedRes => {
            console.log('briefPausedRes: ', briefPausedRes);
            this.focussedBrief.set(briefPausedRes);
          },
          error: err => {
            console.error('briefPausedRes ERROR: ', err);
          }
        });
      }
    }
  }

  async cancelInvites() {
    const confirm = await this.alertService.confirm({
      title: 'Cancel All invites?',
      message: `Are you sure you want to cancel all invites for ${this.focussedBrief()!.title}? This cannot be undone.`,
      confirmText: 'Cancel All invites',
      cancelText: 'Cancel',
      type: 'confirm',
      icon: 'info'
    });

    if (confirm) {
      this.briefService.cancelAllInvites(this.focussedBrief()!.id).pipe(take(1)).subscribe(cancelRes => {
        console.log('cancelRes: ', cancelRes);

        this.filteredApplicants.set(undefined);
        const filters = this.buildApplicantFilters();
        this.creatorFilters = filters;
        this.getBriefApplicants(this.focussedBrief()!.id, filters, 0, true);

        this.toastNotificationService.alert({
          title: 'Invites cancelled',
          message: 'All invites have been cancelled',
          type: 'success',
          icon: 'success'
        });
      }, err => {
        console.warn('Error cancelRes: ', err);
        this.toastNotificationService.alert({
          title: 'Error cancelling invites',
          message: 'An error occurred while cancelling invites',
          type: 'error',
          icon: 'error'
        });
      })
    }
  }

  async declineApplicants() {
    if (this.focussedBrief()) {
      const confirm = await this.alertService.confirm({
        title: 'Decline All Applicants & Remove All Invites?',
        message: `Are you sure you want to decline all applicants and remove all invites for ${this.focussedBrief()!.title}? This cannot be undone.`,
        confirmText: 'Decline All',
        cancelText: 'Cancel',
        type: 'confirm',
        icon: 'info'
      });
      if (confirm) {
        this.briefService.declineOutstandingApplicants(this.focussedBrief()!.id).pipe(take(1)).subscribe({
          next: declineRes => {
            console.log('declineRes: ', declineRes);

            this.filteredApplicants.set(undefined);
            const filters = this.buildApplicantFilters();
            this.creatorFilters = filters;
            this.getBriefApplicants(this.focussedBrief()!.id, filters, 0, true);

            this.toastNotificationService.alert({
              title: 'Applicants declined',
              message: 'All applicants have been declined',
              type: 'success',
              icon: 'success'
            });
          },
          error: err => {
            console.error('declineRes ERROR: ', err);
            this.toastNotificationService.alert({
              title: 'Error declining applicants',
              message: 'An error occurred while declining applicants',
              type: 'error',
              icon: 'error'
            });
          }
        });
      }
    }
  }


  async removeAllHired() {
    if (this.focussedBrief()) {
      const confirm = await this.alertService.confirm({
        title: 'Un-Hire All Applicants?',
        message: `Are you sure you want to un-hire all applicants for ${this.focussedBrief()!.title}? This cannot be undone.`,
        confirmText: 'Un-Hire All',
        cancelText: 'Cancel',
        type: 'confirm',
        icon: 'info'
      });
      if (confirm) {
        this.briefService.unhireAllApplicants(this.focussedBrief()!.id).pipe(take(1)).subscribe({
          next: declineRes => {
            console.log('unhireRes: ', declineRes);
            this.filteredApplicants.set(undefined);
            const filters = this.buildApplicantFilters();
            this.creatorFilters = filters;
            this.getBriefApplicants(this.focussedBrief()!.id, filters, 0, true);

            this.toastNotificationService.alert({
              title: 'Applicants un-hired',
              message: 'All applicants have been un-hired',
              type: 'success',
              icon: 'success'
            });
          },
          error: err => {
            console.error('unhireRes ERROR: ', err);
            this.toastNotificationService.alert({
              title: 'Error un-hiring applicants',
              message: 'An error occurred while un-hiring applicants',
              type: 'error',
              icon: 'error'
            });
          }
        });
      }
    }
  }

  private captureViewSubmissionReturnContext(): void {
    const brief = this.focussedBrief();
    if (!brief?.id) {
      return;
    }
    this.viewSubmissionReturnContext = {
      briefId: brief.id,
      tab: this.uiSelectedTab(),
      subTab: this.uiSelectedSubTab(),
    };
  }

  consumeViewSubmissionReturnContext(expectedBriefId: string): { briefId: string; tab: BriefProgressEnum; subTab: number } | null {
    const ctx = this.viewSubmissionReturnContext;
    this.viewSubmissionReturnContext = null;
    if (!ctx || ctx.briefId !== expectedBriefId) {
      return null;
    }
    return ctx;
  }

  private navigateToViewSubmission(applicant: BriefApplicationResponseDto): void {
    if (!applicant.creator) {
      return;
    }
    this.captureViewSubmissionReturnContext();
    this.router.navigate([`/view-submission/${this.focussedBrief()!.id}/${applicant.creator.blazeFirebaseUid}`]);
  }

  actOnApplicationStatus(applicant: BriefApplicationResponseDto, actionType: ActionTypesEnum) {
    console.log('actOnApplicationStatus()', applicant, actionType);
    console.log('Applicant: ', applicant);
    console.log('Action Type: ', actionType);
    const applicationStatus: number | undefined = applicant.applicationStatus;

    console.log('applicationStatus: ', applicationStatus);

    if (applicationStatus && applicant && applicant.creator) {
      switch (applicationStatus) {
        case 1: // Invited
          this.removeInvitation(applicant)
          break;
        case 2: // Applied
          if (actionType === ActionTypesEnum.HIRE) {
            this.openItemisedHiring(applicant);
          } else if (actionType === ActionTypesEnum.DECLINE_APPLICATION) {
          }
          break;
        case 3: // Hired
          this.centralManagement.focussedBriefApplication.set(applicant);
          console.log('>>>>>>>>>>>>> REMOVE_HIRE 1: ', actionType)
          if (actionType === ActionTypesEnum.REMOVE_HIRE) {
            console.log('>>>>>>>>>>>>> REMOVE_HIRE 2')

            // this.getStreamService.shouldShow = false;

          } else {
            this.navigateToViewSubmission(applicant);
          }
          break;
        case 4: // ReadyToPost
          this.centralManagement.focussedBriefApplication.set(applicant);
          this.navigateToViewSubmission(applicant);
          break;
        case 5: // Posted
          this.centralManagement.focussedBriefApplication.set(applicant);
          this.navigateToViewSubmission(applicant);
          break;
        case 6: // Completed
          this.centralManagement.focussedBriefApplication.set(applicant);
          this.navigateToViewSubmission(applicant);
          break;
        case 7: // Paid
          this.centralManagement.focussedBriefApplication.set(applicant);
          this.navigateToViewSubmission(applicant);
          break;
        case 8:
          this.centralManagement.focussedBriefApplication.set(applicant);

          console.log('>>>>>>>>>>>>> REMOVE_HIRE 1: ', actionType)
          if (actionType === ActionTypesEnum.REMOVE_HIRE) {
            console.log('>>>>>>>>>>>>> REMOVE_HIRE 2')

            // this.getStreamService.shouldShow = false
          } else {
            this.navigateToViewSubmission(applicant);
          }
          break;
        case ApplicationStatusEnum.InviteRemoved:
          break;
        case ApplicationStatusEnum.ApplicationDeclined:
          this.revertApplicationDecline(applicant.id);
          break;
        case ApplicationStatusEnum.HiredRemoved:
          // this.openItemisedHiring(applicant);
          // this.getStreamService.shouldShow = false
          break;
        default:
          break;
      }
    }
  }

  async removeInvitation(applicant: BriefApplicationResponseDto) {
    console.log('>>>>> removeInvitation', applicant);
    if (applicant && applicant.creator) {
      const confirm = await this.alertService.confirm({
        title: `Uninvite ${applicant.creator.firstName}?`,
        message: `Are you sure you want to uninvite ${applicant.creator.firstName} from ${this.focussedBrief()?.title ?? 'this brief'}?`,
        confirmText: 'Yes, remove invite',
        cancelText: 'Cancel',
        type: 'confirm',
        icon: 'warning'
      });

      if (confirm) {
        this.actionsProcessing.set([...this.actionsProcessing(), applicant.id]);
        this.briefApplicationService.removeCreatorInvite(applicant.id).pipe(take(1)).subscribe({
          next: (uninviteRes: any) => {
            console.log('uninviteRes: ', uninviteRes);
            applicant.applicationStatus = ApplicationStatusEnum.InviteRemoved;
            applicant!.applicationStatusModifiedDate = uninviteRes.applicationStatusModifiedDate || new Date();

            if (this.filteredApplicants() && this.filteredApplicants()!.length > 0) {
              const foundIndex = this.filteredApplicants()!.findIndex(appy => appy.id === applicant.id);
              if (foundIndex !== -1) {
                // Update the signal by creating a new array with the updated object
                const updatedApplicants = [...this.filteredApplicants()!];
                updatedApplicants[foundIndex] = {
                  ...updatedApplicants[foundIndex],
                  applicationStatus: ApplicationStatusEnum.InviteRemoved,
                  applicationStatusModifiedDate: uninviteRes.applicationStatusModifiedDate || new Date()
                };
                this.filteredApplicants.set(updatedApplicants);
              }
            }

            this.actionsProcessing.set(this.actionsProcessing().filter(id => id !== applicant.id));
            this.loadAssignmentSummary();
            this.toastNotificationService.alert({
              title: 'Creator uninvited',
              message: 'Creator has been uninvited from the brief',
              type: 'success',
              icon: 'success'
            });
          },
          error: (err: any) => {
            console.warn('error uninviting creator: ', err);
            this.actionsProcessing.set(this.actionsProcessing().filter(id => id !== applicant.id));
          }
        });
      }
    }
  }

  openItemisedHiring(applicant: BriefApplicationResponseDto) {
    console.log('openItemisedHiring()', applicant);
    console.log('isOpen: ', this.centralManagement.isItemisedHireOpen());
    if (this.centralManagement.isItemisedHireOpen()) {
      this.hireCreator(applicant);
    } else {
      this.centralManagement.isItemisedHireOpen.set(true);
      this.centralManagement.itemisedHireDataSginal.set({
        briefApplication: applicant,
        brief: this.focussedBrief()!,
        creator: applicant.creator
      });
    }
    console.log('isOpen - AFTER: ', this.centralManagement.isItemisedHireOpen());
  }

  /**
   * The cross-post channels to commit for this hire.
   *
   * Returns undefined — rather than [] — when the brief offers no cross-posts, so the
   * field is omitted from the request entirely and nothing changes for briefs that
   * have never had cross-platform options.
   */
  private resolveAcceptedCrossPlatforms(
    applicant: BriefApplicationResponseDto,
  ): number[] | undefined {
    const brief = this.focussedBrief();
    if (brief?.pieces?.length) {
      return this.acceptedPieceChannelIds(applicant);
    }
    const offered = togglableChannels(applicant, brief?.shoutBrief);
    if (offered.length === 0) {
      return undefined;
    }
    const draft = this.centralManagement.crossPlatformDraftFor(applicant.id, offered);
    return offered.filter((channel) => draft.includes(channel));
  }

  private offeredPieceChannelIds(applicant: BriefApplicationResponseDto): number[] {
    const brief = this.focussedBrief();
    if (!brief?.pieces?.length) {
      return [];
    }
    const offered = new Set(readChannels(applicant.crossPlatformsPutForward));
    return socialChannelIdsOnPieces(brief.pieces).filter((channelId) => offered.has(channelId));
  }

  /** Channels the brand currently accepts on a piece-list hire, from the shared draft. */
  acceptedPieceChannelIds(applicant: BriefApplicationResponseDto): number[] {
    const offeredOnPieces = this.offeredPieceChannelIds(applicant);
    const draft = this.centralManagement.crossPlatformDraftFor(applicant.id, offeredOnPieces);
    return offeredOnPieces.filter((channelId) => draft.includes(channelId));
  }

  /**
   * What a piece-list hire costs the brand, VAT included, in major units.
   *
   * Hired: the total frozen at hire. Applied: the server quote for the channels
   * currently accepted, or null until it has come back. Studio never prices pieces
   * itself, so there is no client figure to fall back on.
   */
  pieceHireTotal(applicant: BriefApplicationResponseDto): number | null {
    if (!this.focussedBrief()?.pieces?.length) {
      return null;
    }
    const frozen = hiredBrandTotal(applicant);
    if (frozen != null) {
      return toMajor(frozen);
    }
    const quote = this.pieceQuotes.quoteFor(applicant.id, this.acceptedPieceChannelIds(applicant));
    return quote ? toMajor(quote.quote.brandTotal) : null;
  }

  hireCreator(applicant: BriefApplicationResponseDto) {
    if (this.actionsProcessing().includes(applicant.id)) {
      return;
    }

    console.log('hireCreator()', applicant);
    const brief = this.focussedBrief();
    const isPieceHire = !!brief?.pieces?.length;
    // The cross-post channels the brand left switched on, read from the one draft the
    // table's tiles and the hire drawer's checkboxes both edit. Undefined when the brief
    // has no cross-post options, so nothing changes for single-network briefs.
    const acceptedCrossPlatforms = this.resolveAcceptedCrossPlatforms(applicant);
    // The total the brand was shown. The API refuses the hire if its own price differs.
    const quoted = isPieceHire
      ? this.pieceQuotes.quoteFor(applicant.id, acceptedCrossPlatforms ?? [])
      : null;
    if (quoted && quoted.quote.owed.length === 0) {
      this.alertNothingOwed();
      return;
    }

    this.actionsProcessing.set([...this.actionsProcessing(), applicant.id]);
    const hireCreator: HireCreatorDto = {
      briefId: applicant.briefId,
      briefApplicationId: applicant.id,
      creatorUserId: (applicant.creator && applicant.creator.blazeFirebaseUid) ? applicant.creator.blazeFirebaseUid : '',
      hiredPricing: brief!.currentPrice,
      createdBy: this.firebaseService.userId! || '',
      ...(acceptedCrossPlatforms ? { acceptedCrossPlatforms } : {}),
      ...(quoted ? { expectedBrandTotal: quoted.quote.brandTotal } : {}),
    }
    this.briefApplicationService.hireCreator(hireCreator).pipe(take(1)).subscribe({
      next: (hireCreatorRes: HireCreatorResponseDto) => {
        console.log('hireCreatorRes 2: ', hireCreatorRes);

        if (isInsufficientFundsHireResponse(hireCreatorRes)) {
          this.actionsProcessing.set(this.actionsProcessing().filter(id => id !== applicant.id));
          void this.promptTopUpAfterInsufficientHire();
          return;
        }

        const hiredApplication = hireCreatorRes as BriefApplicationResponseDto;
        const wasApplied = applicant.applicationStatus === ApplicationStatusEnum.Applied;

        // What was reserved, worked out before the draft it is read from is cleared.
        // A piece hire carries its frozen total; a legacy hire is quoted the old way.
        const reservedPence = isPieceHire
          ? (hiredBrandTotal(hiredApplication) ?? quoted?.quote.brandTotal ?? null)
          : null;
        const committed = reservedPence != null
          ? toMajor(reservedPence)
          : this.legacyHireCommitted(applicant, acceptedCrossPlatforms);

        applicant!.applicationStatus = hiredApplication.applicationStatus;
        applicant!.applicationStatusModifiedDate = hiredApplication.applicationStatusModifiedDate;

        // Only the hired row changes: no list, count or balance refetch.
        const rows = this.filteredApplicants();
        const foundIndex = rows?.findIndex(appy => appy.id === applicant.id) ?? -1;
        if (rows && foundIndex !== -1) {
          const updatedApplicants = [...rows];
          updatedApplicants[foundIndex] = mergeHiredApplication(
            updatedApplicants[foundIndex],
            hiredApplication,
            acceptedCrossPlatforms,
          );
          this.filteredApplicants.set(updatedApplicants);
        }
        if (wasApplied) {
          this.filterStats.update(filterStatsAfterHire);
        }

        // The decision is committed; the draft and its quotes have served their purpose.
        this.centralManagement.clearCrossPlatformDraft(applicant.id);
        this.pieceQuotes.forget(applicant.id);

        this.actionsProcessing.set(this.actionsProcessing().filter(id => id !== applicant.id));
        this.centralManagement.portfolioAction.set(null);
        this.centralManagement.isItemisedHireOpen.set(false);

        const businessAccountId = brief!.businessAccountId;
        if (reservedPence != null) {
          this.walletService.reserve(businessAccountId, toMajor(reservedPence));
        } else {
          // Legacy hires have no server total to subtract, so the balance is re-read quietly.
          this.walletService.getBalance(businessAccountId).subscribe({
            error: (err: HttpErrorResponse) => console.error('Error re-fetching balances ', err),
          });
        }

        // A piece list has no primary network: only the accepted channels are networks.
        const networkCount = isPieceHire
          ? (acceptedCrossPlatforms?.length ?? 0)
          : (acceptedCrossPlatforms?.length ?? 0) + 1;
        this.toastNotificationService.alert({
          title: 'Creator hired',
          // The money belongs in the confirmation: the brand just committed it, and
          // the modal that showed the figure is gone by the time this appears.
          message: this.hiredToastMessage(applicant, isPieceHire, networkCount, committed),
          type: 'success',
          icon: 'success'
        });
      },
      error: (err: unknown) => {
        console.warn('error hiring creator 2: ', err);
        this.actionsProcessing.set(this.actionsProcessing().filter(id => id !== applicant.id));
        if (isInsufficientFundsHireError(err)) {
          void this.promptTopUpAfterInsufficientHire();
          return;
        }
        const code = apiErrorCode(err);
        if (code === 'NOTHING_OWED') {
          this.alertNothingOwed();
          return;
        }
        const repriced = priceChangedQuote(err);
        if (repriced) {
          // Show the new price where the old one was; the next Hire confirms it.
          this.pieceQuotes.accept(applicant.id, acceptedCrossPlatforms ?? [], {
            currencyCode: quoted?.currencyCode ?? brief?.currentPrice?.currencyCode ?? 'gbp',
            quote: repriced,
          });
          this.pieceQuotes.markPriceChanged(applicant.id);
          this.toastNotificationService.alert({
            title: 'Price updated',
            message: `The price is now ${this.formatMoney(toMajor(repriced.brandTotal))}. Check it and hire again to confirm.`,
            type: 'warning',
            icon: 'warning'
          });
          return;
        }
        this.toastNotificationService.alert({
          title: 'Could not hire creator',
          message: (err as HttpErrorResponse)?.error?.message ?? 'Please try again.',
          type: 'error',
          icon: 'error'
        });
      }
    });
  }

  private alertNothingOwed(): void {
    this.toastNotificationService.alert({
      title: 'Nothing to hire',
      message: `${NOTHING_OWED_COPY}.`,
      type: 'warning',
      icon: 'warning'
    });
  }

  private hiredToastMessage(
    applicant: BriefApplicationResponseDto,
    isPieceHire: boolean,
    networkCount: number,
    committed: number,
  ): string {
    const name = applicant.creator?.firstName ?? 'Creator';
    if (isPieceHire) {
      const where = networkCount > 0
        ? `on ${networkCount} ${networkCount === 1 ? 'network' : 'networks'}`
        : 'for UGC delivery';
      return `${name} hired ${where} · ${this.formatMoney(committed)} committed`;
    }
    return networkCount > 1
      ? `${name} hired on ${networkCount} networks · ${this.formatMoney(committed)} committed`
      : 'Creator has been hired';
  }

  private formatMoney(amount: number): string {
    const currencyCode = this.focussedBrief()?.currentPrice?.currencyCode?.toUpperCase() || 'GBP';
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: currencyCode }).format(amount);
  }

  /**
   * What a legacy (no piece list) hire just committed, VAT included.
   *
   * Built from the same pieces as the Spend cell and the Review application modal, so
   * all three quote one figure — `getTotalPriceV2` is already VAT-inclusive, and the
   * cross-post prices are grossed up to match.
   */
  private legacyHireCommitted(
    applicant: BriefApplicationResponseDto,
    acceptedCrossPlatforms: number[] | undefined,
  ): number {
    const brief = this.focussedBrief();
    const creator = applicant.creator;

    const primaryGross = this.getTotalPriceV2(applicant) ?? 0;
    const crossNet = brief && creator
      ? crossPostNetTotal(
          (acceptedCrossPlatforms ?? []).map((channel) =>
            this.pricingModelService.getAudiencePrice(channel, brief, creator),
          ),
        )
      : 0;

    return primaryGross + applyVat(crossNet, brief, this.paymentMethod());
  }

  private promptTopUpAfterInsufficientHire(): Promise<void> {
    return openTopUpDrawerForInsufficientFunds(
      this.walletService,
      this.alertService,
      this.focussedBrief(),
      () => this.centralManagement.isItemisedHireOpen.set(false),
    );
  }

  revertApplicationDecline(applicationId: string) {
    const setStatus: { status: ApplicationStatusEnum; user: string; } = {
      status: ApplicationStatusEnum.Applied,
      user: this.firebaseService.userId!
    }

    // const subRevertStatus = this.briefApplicationService.adminSetApplicationStatus(applicationId, setStatus).subscribe({
    //   next: response => {
    //     if (response) {
    //       this.filteredApplicants?.forEach((applicant: BriefApplicationResponseDto) => {
    //         if (applicant.id === response.id) {
    //           applicant.applicationStatus = ApplicationStatusEnum.Applied;
    //         }
    //       });

    //       this._notificationService.sendMessage({
    //         message: 'Applicant un-hired!',
    //         type: NotificationType.success
    //       });
    //     }
    //   },
    //   error: error => {
    //     console.error('Error reverting status', error);
    //     this._notificationService.sendMessage({
    //       message: 'Application status change error!',
    //       type: NotificationType.error
    //     });
    //   }
    // });
    // this.subscriptionList.add(subRevertStatus);
  }

  updateSelectedFilter(filter: ApplicationStatusEnum) {
    console.log('updateSelectedFilter(): ', filter);
    this.filteredApplicants.set(undefined);
    this.selectedFilter.set(filter);

    if (filter !== ApplicationStatusEnum.All) {
      if (filter === ApplicationStatusEnum.Completed) {
        this.magicSearchService.filtersObjectSignal.set({
          ...this.magicSearchService.filtersObjectSignal(),
          applicationStatus: [5, 6, 7, 12]
        });
      } else {
        this.magicSearchService.filtersObjectSignal.set({
          ...this.magicSearchService.filtersObjectSignal(),
          applicationStatus: [filter]
        });
      }
    } else {
      const { applicationStatus, ...rest } = this.magicSearchService.filtersObjectSignal();
      this.magicSearchService.filtersObjectSignal.set(rest);
    }
  }

  getTotalPriceV2(applicant: BriefApplicationResponseDto) {
    const brief = this.focussedBrief();
    if (
      isShoutPlusBrief(brief) &&
      getResolvedPaymentMethodType(brief, this.paymentMethod()) === PaymentMethodTypeEnum.CASH
    ) {
      return this.getTotalPriceShoutPlus();
    }
    if (brief?.pieces?.length) {
      // Piece-list briefs are priced by the server only (VAT included).
      return this.pieceHireTotal(applicant) ?? 0;
    }
    if (brief && applicant && brief.currentPrice && brief.businessAccount) {
      let finalPrice = this.pricingModelService.getTotalPrice(brief, applicant.creator!);
      const noVatAgenciesIds: string[] = [
        environment.shoutAgencyId
      ];
      const agencyId = brief.businessAccount.agencyId;
      if (brief.currentPrice.currencyCode.toUpperCase() == 'GBP' &&
        (!agencyId || !noVatAgenciesIds.includes(agencyId))) {
        finalPrice = finalPrice * 1.2
      }
      return finalPrice;
    } else {
      return 0;
    }
  }

  testAudiencePrice(applicant: BriefApplicationResponseDto) {
    const brief = this.focussedBrief();
    if (brief && applicant && brief.currentPrice && brief.businessAccount) {
      if (brief.shoutBrief) {
        let influencerPrice = this.pricingModelService.getAudiencePrice(brief.shoutBrief.socialPlatform, brief, applicant.creator!);
        return influencerPrice;
      } else {
        return 0;
      }
    } else {
      return 0;
    }
  }
}
